import type { ComponentPublicInstance, MaybeRef, Ref } from 'vue'
import { computed, isRef, onMounted, ref, unref, useAttrs } from 'vue'
import { useLoading } from '../../../composables/useLoading'

export type Option = { label: string; value: string }

export type SelectProps = {
  options?: MaybeRef<Option[]>
  loadOptions?: (filterText: string) => Promise<Option[]>
  ensureSelected?: (selectedIds: string[]) => Promise<Option[]>
  showFilter?: boolean
  maxOptions?: number
}

export function useSelect<T>(model: Ref<T>, props: SelectProps, rootElement: Ref<ComponentPublicInstance | null>) {
  const originalAttrs = useAttrs()
  const options = ref<Option[]>(getInitialOptions(props.options))
  const { load, loading } = buildLoader(props, options, model)

  if (props.ensureSelected && props.loadOptions) {
    const ensureSelected = props.ensureSelected
    onMounted(async () => {
      const ids = selectedIds(model)
      if (ids.length > 0) {
        options.value = await ensureSelected(ids)
      }
    })
  }

  const localModel = computed<T>({
    get: () => model.value,
    set: (value: T) => {
      model.value = value
      setTimeout(() => {
        const el = rootElement.value?.$el as HTMLElement | undefined
        el?.dispatchEvent(new Event('change', { bubbles: true }))
      })
    }
  })

  const attrs = computed(() => ({
    ...originalAttrs,
    modelValue: localModel.value,
    'onUpdate:modelValue': (value: T) => (localModel.value = value),
    options: options.value,
    onBeforeShow: load,
    optionLabel: 'label',
    optionValue: 'value',
    showClear: true
  }))

  function labelOf(value: string) {
    const known = props.options ? unref(props.options) : options.value
    return known.find(x => x.value === value)?.label
  }

  return { attrs, loading, loadOptions: load, labelOf }
}

function getInitialOptions(options: MaybeRef<Option[]> | undefined): Option[] {
  return !options ? [] : isRef(options) ? options.value : options
}

function selectedIds<T>(model: Ref<T>): string[] {
  return Array.isArray(model.value) ? model.value : model.value ? [model.value as string] : []
}

function buildLoader<T>(props: SelectProps, options: Ref<Option[]>, model: Ref<T>) {
  if (props.loadOptions) {
    const loadOptions = props.loadOptions
    return useLoading(async (filterText: string | undefined) => {
      options.value = []
      options.value = await loadOptions(filterText ?? '')
    })
  }

  if (props.options) {
    return {
      load: (filterText: string | undefined) => {
        const source = unref(props.options) ?? []
        const filter = filterText?.toLocaleLowerCase()
        const matching = filter ? source.filter(x => x.label.toLocaleLowerCase().includes(filter)) : source
        options.value =
          props.maxOptions === undefined ? matching : limitKeepingSelected(matching, source, model, props.maxOptions)
      },
      loading: false
    }
  }

  return { load: () => {}, loading: false }
}

function limitKeepingSelected<T>(matching: Option[], source: Option[], model: Ref<T>, maxOptions: number): Option[] {
  const selected = new Set(selectedIds(model))
  const selectedOptions = source.filter(x => selected.has(x.value))
  return [...selectedOptions, ...matching.filter(x => !selected.has(x.value)).slice(0, maxOptions)]
}
